import React, { useState, useRef } from 'react';
import {
  Database,
  CheckCircle,
  Circle,
  ArrowUp,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type QueryStep = 'idle' | 'scanning' | 'building' | 'running' | 'complete' | 'error';

interface QueryPanelProps {
  databaseName: string;
  currentQuery: string;
  queryStep: QueryStep;
  onSendQuery: (query: string) => void;
  isProcessing: boolean;
  selectedModel: string;
  onModelChange: (model: string) => void;
  className?: string;
}

const AVAILABLE_MODELS = [
  { id: 'gpt-4o', name: 'GPT-4o' },
  { id: 'gpt-4', name: 'GPT-4' },
  { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' },
];

// GPT Logo Icon Component
const GPTIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4066-.6898zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" fill="currentColor"/>
  </svg>
);

// Database Icon Component
const DatabaseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="8" cy="3.5" rx="5.5" ry="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M2.5 3.5v9c0 1.1 2.46 2 5.5 2s5.5-.9 5.5-2v-9" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M2.5 8c0 1.1 2.46 2 5.5 2s5.5-.9 5.5-2" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

export function QueryPanel({
  databaseName,
  currentQuery,
  queryStep,
  onSendQuery,
  isProcessing,
  selectedModel,
  onModelChange,
  className
}: QueryPanelProps) {
  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = () => {
    const query = inputValue.trim();
    if (!query || isProcessing) return;
    onSendQuery(query);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.key === 'Enter' && !e.shiftKey) || ((e.metaKey || e.ctrlKey) && e.key === 'Enter')) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const getStepStatus = (step: 'scanning' | 'building' | 'running') => {
    const stepOrder = ['idle', 'scanning', 'building', 'running', 'complete'];
    const currentIndex = stepOrder.indexOf(queryStep);
    const stepIndex = stepOrder.indexOf(step);

    if (queryStep === 'error') return 'error';
    if (queryStep === 'complete') return 'complete';
    if (currentIndex > stepIndex) return 'complete';
    if (currentIndex === stepIndex) return 'active';
    return 'pending';
  };

  const renderStepIcon = (status: 'complete' | 'active' | 'pending' | 'error') => {
    if (status === 'complete') {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    if (status === 'active') {
      return <Loader2 className="h-4 w-4 text-[#6d2afe] animate-spin" />;
    }
    return <Circle className="h-4 w-4 text-[#22212d]/30" />;
  };

  const selectedModelData = AVAILABLE_MODELS.find(m => m.id === selectedModel) || AVAILABLE_MODELS[0];

  return (
    <div className={cn("w-[320px] bg-white border-l border-[#e6e6ea] flex flex-col shrink-0", className)}>
      {/* Header - Database Name */}
      <div className="px-6 py-5 border-b border-[#e6e6ea] shrink-0">
        <div className="flex items-center gap-3">
          <div className="text-[#22212d]">
            <DatabaseIcon />
          </div>
          <span className="text-[15px] font-medium text-[#22212d]">{databaseName || 'Select Database'}</span>
        </div>
      </div>

      {/* Query Display & Progress Steps */}
      <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar">
        {/* Current Query Display */}
        {currentQuery && (
          <div className="mb-6">
            <p className="text-[14px] text-[#22212d] leading-relaxed">{currentQuery}</p>
          </div>
        )}

        {/* Progress Steps */}
        {(queryStep !== 'idle' || currentQuery) && (
          <div className="space-y-2">
            <p className="text-[13px] text-[#22212d]/60 mb-4">
              I'll analyse your prompt and scan your database to build a query
            </p>

            {/* Step: Scanning database */}
            <div className="flex items-center gap-3">
              {renderStepIcon(getStepStatus('scanning'))}
              <span className={cn(
                "text-[13px]",
                getStepStatus('scanning') === 'complete' ? "text-[#22212d]" : "text-[#22212d]/60"
              )}>
                Scanning database
              </span>
            </div>

            {/* Step: Building query */}
            <div className="flex items-center gap-3">
              {renderStepIcon(getStepStatus('building'))}
              <span className={cn(
                "text-[13px]",
                getStepStatus('building') === 'complete' ? "text-[#22212d]" : "text-[#22212d]/60"
              )}>
                Building query
              </span>
            </div>

            {/* Step: Running query */}
            <div className="flex items-center gap-3">
              {renderStepIcon(getStepStatus('running'))}
              <span className={cn(
                "text-[13px]",
                getStepStatus('running') === 'complete' ? "text-[#22212d]" : "text-[#22212d]/60"
              )}>
                Running query
              </span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {queryStep === 'idle' && !currentQuery && (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <Database className="h-12 w-12 text-[#22212d]/20 mb-4" />
            <p className="text-[14px] text-[#22212d]/50 mb-2">Ask anything about your data</p>
            <p className="text-[12px] text-[#22212d]/30">Type your question below to get started</p>
          </div>
        )}
      </div>

      {/* Input Footer */}
      <div className="px-3 py-4 border-t border-[#e6e6ea] shrink-0">
        <div className="bg-[#f8f8fa] rounded-xl p-3">
          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything in your DB"
            disabled={isProcessing}
            className="w-full bg-transparent text-[14px] text-[#22212d] placeholder:text-[#22212d]/40 resize-none outline-none min-h-[40px] max-h-[120px]"
            rows={2}
          />

          {/* Footer Row: Model Selector + Send Button */}
          <div className="flex items-center justify-between mt-2">
            {/* Model Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-[#e8e8ec] transition-colors outline-none">
                <GPTIcon />
                <span className="text-[13px] text-[#22212d]">{selectedModelData.name}</span>
                <ChevronDown className="h-3.5 w-3.5 text-[#22212d]/60" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-white border-[#e6e6ea]">
                {AVAILABLE_MODELS.map((model) => (
                  <DropdownMenuItem
                    key={model.id}
                    onClick={() => onModelChange(model.id)}
                    className={cn(
                      "text-[13px]",
                      selectedModel === model.id && "bg-[#f3f3f6]"
                    )}
                  >
                    {model.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Send Button */}
            <button
              onClick={handleSubmit}
              disabled={isProcessing || !inputValue.trim()}
              className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center transition-colors",
                isProcessing || !inputValue.trim()
                  ? "bg-[#e8e8ec] text-[#22212d]/30 cursor-not-allowed"
                  : "bg-[#6d2afe] text-white hover:bg-[#5a1ee6]"
              )}
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QueryPanel;
